import axios, {
  AxiosError,
  AxiosInterceptorManager,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'js-cookie';

import { isTokenExpired } from '@/utils';

type RequestUseInterceptorsParams = Parameters<
  AxiosInterceptorManager<InternalAxiosRequestConfig>['use']
>;

type ResponseUseInterceptorsParams = Parameters<
  AxiosInterceptorManager<AxiosResponse>['use']
>;

const refreshTokens = async () => {
  const refreshToken = Cookies.get('refresh_token');
  if (!refreshToken) throw new Error('Missing refresh_token');

  const response = await axios.post(
    `${ENV.API_URL}/api/auth/refresh-token`,
    JSON.stringify({ refreshToken }),
  );

  const { accessToken, refreshToken: newRefreshToken } = response.data.data;

  if (!accessToken || !newRefreshToken) {
    throw new Error('Invalid token response from server');
  }

  Cookies.set('access_token', accessToken, {
    domain: ENV.WHAT_ABILITY_DOMAIN,
    expires: 1,
  });

  Cookies.set('refresh_token', newRefreshToken, {
    domain: ENV.WHAT_ABILITY_DOMAIN,
    expires: 30,
  });

  return accessToken;
};

const requestOnFulfilled: RequestUseInterceptorsParams[0] = async (
  config: InternalAxiosRequestConfig,
) => {
  let token = Cookies.get('access_token');

  if (token && isTokenExpired(token)) {
    token = await refreshTokens();
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const requestOnRejected: RequestUseInterceptorsParams[1] = (
  error: AxiosError,
) => Promise.reject(error);

const responseOnFulfilled: ResponseUseInterceptorsParams[0] = (response) =>
  response;

const responseOnRejected: ResponseUseInterceptorsParams[1] = (
  error: AxiosError,
) => {
  if (error.response?.status === 401) {
    const path = window.location.pathname;
    const isPublicAuthPage =
      path.startsWith('/admin/') &&
      /(login|forgot-password|reset-password|otp)/.test(path);

    if (!isPublicAuthPage) {
      window.location.href = '/admin/login';
    }
  }

  return Promise.reject(error);
};

const baseAxiosConfig: CreateAxiosDefaults = {
  headers: {
    'Content-Type': 'application/json',
    'X-App-Platform': 'web',
  },
};

const authAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.API_URL}/api`,
  withCredentials: true,
});

authAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected
);

authAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

export { authAxiosClient };
